import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";
import { Phone, Building2, User, Loader2, AlertTriangle, MapPin, X, Pin, Link2, Globe } from "lucide-react";
import { toast } from "sonner";

// ANI field with ALI subscriber typeahead: type digits or a name → pick → fills phone + caller name (+ address if empty).
// Also surfaces repeat-caller / open-call flags, pinned caller notes and a one-tap last-known-address offer.
export const AniInput = ({ value, onChange, onPick, onUseAddress, onAttach, onUseName, attaching = false, className = "", testid = "call-caller-phone" }) => {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hist, setHist] = useState(null);
  const [offer, setOffer] = useState(null);
  const [notes, setNotes] = useState([]);
  const [addingNote, setAddingNote] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [cid, setCid] = useState(null);
  const intl = (value || "").trim().startsWith("+");
  useEffect(() => {
    const raw = (value || "").trim();
    const dg = raw.replace(/\D/g, "");
    if (dg.length < 10 || (intl && dg.length < 8)) { setCid(null); return; }
    const h = setTimeout(() => client.get("/ali/caller-id", { params: { number: raw } }).then((r) => setCid(r.data)).catch(() => setCid(null)), 450);
    return () => clearTimeout(h);
  }, [value]);
  const box = useRef(null);
  const picked = useRef(false);
  const digits = (value || "").replace(/\D/g, "");
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 3) { setSugs([]); return; }
    if (picked.current) { picked.current = false; return; }
    setBusy(true);
    const h = setTimeout(() => client.get("/ali/suggest", { params: { q } })
      .then((r) => { setSugs(r.data.suggestions || []); setOpen(true); })
      .catch(() => setSugs([])).finally(() => setBusy(false)), 220);
    return () => clearTimeout(h);
  }, [value]);
  useEffect(() => {
    if (digits.length < 10) { setHist(null); setOffer(null); return; }
    const h = setTimeout(() => client.get("/ali/history", { params: { digits } }).then((r) => { setHist(r.data); setNotes(r.data.notes || []); }).catch(() => setHist(null)), 300);
    return () => clearTimeout(h);
  }, [digits]);
  useEffect(() => {
    const f = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", f); return () => document.removeEventListener("mousedown", f);
  }, []);
  const pick = (s) => {
    picked.current = true;
    onChange(s.phone); onPick?.(s); setOpen(false);
    if (s.kind === "prior caller" && onUseAddress) {
      client.get("/ali/history", { params: { digits: s.digits } }).then((r) => { setHist(r.data); if (r.data.last_address?.address) setOffer(r.data.last_address); }).catch(() => {});
    }
  };
  const flag = hist && (hist.repeat || hist.open_calls?.length > 0);
  const refreshHist = () => client.get("/ali/history", { params: { digits } }).then((r) => { setHist(r.data); setNotes(r.data.notes || []); }).catch(() => {});
  const addNote = async () => {
    if (!noteText.trim()) return;
    try { await client.post("/ali/notes", { digits, text: noteText.trim() }); setNoteText(""); setAddingNote(false); toast.success("Note pinned to this number"); refreshHist(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not pin note"); }
  };
  const delNote = async (id) => {
    try { await client.delete(`/ali/notes/${id}`); refreshHist(); } catch { toast.error("Could not remove note"); }
  };
  return (
    <div ref={box} className="relative">
      <div className="relative">
        <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} onFocus={() => sugs.length && setOpen(true)}
          placeholder="Automatic Number ID — type digits or business name" className={`${className} pr-8`} autoComplete="off" />
        <span className="absolute right-2 top-1/2 -translate-y-1/2">{busy ? <Loader2 size={13} className="animate-spin text-[#9CA3AF]" /> : <Phone size={13} className="text-[#6B7280]" />}</span>
      </div>
      {cid && (
        <div data-testid="ani-caller-id" className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 px-2 py-1 border font-mono text-[10px] ${cid.is_foreign ? "border-[#22D3EE]/60 bg-[#22D3EE]/10 text-[#A5F3FC]" : "border-[#2C2D35] bg-[#141518] text-[#D1D5DB]"}`}>
          <Globe size={11} className="shrink-0" />
          <span className="font-bold uppercase text-white">Caller ID</span>
          {cid.name && <span className="text-white">{cid.name}{cid.caller_type ? <span className="text-[#9CA3AF]"> ({cid.caller_type})</span> : null}</span>}
          <span>{cid.international}</span>
          <span>{cid.country}{cid.location ? ` · ${cid.location}` : ""}</span>
          {cid.carrier && <span>· {cid.carrier}</span>}
          <span>· {cid.line_type}</span>
          {cid.is_foreign && <span className="px-1 font-bold border border-[#22D3EE]/60">INTERNATIONAL</span>}
          {!cid.valid && <span className="px-1 font-bold border border-[#EF4444]/60 text-[#FCA5A5]">INVALID NUMBER</span>}
          <span className="text-[#6B7280]" title={cid.provider_note || cid.provider_error || ""}>· {cid.source === "twilio_lookup" ? "CNAM" : cid.source === "ali" ? "ALI" : "offline db"}</span>
          {cid.name && onUseName && <button type="button" data-testid="ani-use-name" onClick={() => onUseName(cid.name)} className="ml-auto px-1.5 py-0.5 font-head font-bold bg-[#3B82F6] text-white hover:bg-[#2563eb]">USE NAME</button>}
        </div>
      )}
      {flag && (
        <div data-testid="ani-repeat-flag" className={`mt-1 flex flex-wrap items-center gap-1.5 px-2 py-1 border font-mono text-[10px] animate-pulse min-w-[420px] relative z-10 ${hist.open_calls.length ? "border-[#EF4444] bg-[#EF4444]/15 text-[#FCA5A5]" : "border-[#F59E0B] bg-[#F59E0B]/15 text-[#FCD34D]"}`}>
          <AlertTriangle size={12} className="shrink-0" />
          <span className="font-bold uppercase">{hist.open_calls.length ? "OPEN CALL ON THIS NUMBER" : "REPEAT CALLER"}</span>
          <span className="text-white/80">· {hist.prior_calls} prior call{hist.prior_calls === 1 ? "" : "s"}</span>
          {hist.open_calls.slice(0, 2).map((o) => (
            <span key={o.cc_number} className="ml-1 inline-flex items-center gap-1 px-1 border border-current/40">
              {o.cad_number || o.cc_number} · {o.call_type || o.status}
              {onAttach && <button type="button" data-testid={`ani-attach-${o.cc_number}`} onClick={() => onAttach(o)} disabled={attaching}
                className="ml-1 px-1.5 py-0.5 font-head font-bold bg-[#EF4444] text-white hover:bg-[#dc2626] disabled:opacity-50 flex items-center gap-0.5"><Link2 size={10} /> ATTACH</button>}
            </span>
          ))}
        </div>
      )}
      {digits.length >= 10 && (
        <div data-testid="ani-notes" className="mt-1 space-y-1">
          {(hist?.notes || notes).map((n) => (
            <div key={n.id} data-testid={`ani-note-${n.id}`} className="flex items-start gap-1.5 px-2 py-1 border border-[#A78BFA]/50 bg-[#A78BFA]/10 font-mono text-[10px] text-[#DDD6FE]">
              <Pin size={11} className="shrink-0 mt-0.5 text-[#C4B5FD]" />
              <span className="flex-1 min-w-0"><span className="text-white">{n.text}</span> <span className="text-[#9CA3AF]">· {n.user} · {(n.ts || "").slice(0, 10)}</span></span>
              <button type="button" data-testid={`ani-note-del-${n.id}`} onClick={() => delNote(n.id)} className="text-[#9CA3AF] hover:text-white"><X size={11} /></button>
            </div>
          ))}
          {addingNote ? (
            <div className="flex gap-1">
              <input autoFocus data-testid="ani-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNote(); if (e.key === "Escape") setAddingNote(false); }}
                placeholder='e.g. "hard of hearing", "frequent false alarms"' className="flex-1 bg-[#0F1012] border border-[#A78BFA]/60 px-2 py-1 font-mono text-[10px] text-white outline-none" />
              <button type="button" data-testid="ani-note-save" onClick={addNote} disabled={!noteText.trim()} className="px-2 font-head font-bold text-[10px] bg-[#A78BFA] text-black disabled:opacity-50">PIN</button>
              <button type="button" onClick={() => setAddingNote(false)} className="px-1.5 text-[#9CA3AF] hover:text-white"><X size={12} /></button>
            </div>
          ) : (
            <button type="button" data-testid="ani-note-add" onClick={() => setAddingNote(true)} className="flex items-center gap-1 font-mono text-[9px] uppercase text-[#C4B5FD] hover:text-white">
              <Pin size={10} /> Pin a note to this number
            </button>
          )}
        </div>
      )}
      {offer && (
        <div data-testid="ani-address-offer" className="mt-1 flex items-center gap-2 px-2 py-1.5 border border-[#3B82F6]/60 bg-[#3B82F6]/10 font-mono text-[10px] text-[#BFDBFE]">
          <MapPin size={12} className="shrink-0 text-[#93C5FD]" />
          <span className="min-w-0 flex-1 truncate">Last known: <span className="text-white">{offer.address}</span> <span className="text-[#9CA3AF]">· {offer.cc_number} · {(offer.when || "").slice(0, 10)}</span></span>
          <button type="button" data-testid="ani-use-address" onClick={() => { onUseAddress(offer); setOffer(null); }}
            className="px-2 py-0.5 font-head font-bold bg-[#3B82F6] text-white hover:bg-[#2563eb]">USE ADDRESS</button>
          <button type="button" data-testid="ani-dismiss-address" onClick={() => setOffer(null)} className="text-[#9CA3AF] hover:text-white"><X size={12} /></button>
        </div>
      )}
      {open && sugs.length > 0 && (
        <div data-testid="ani-suggestions" className="absolute z-50 left-0 right-0 mt-1 bg-[#0F1012] border border-[#2C2D35] shadow-2xl max-h-64 overflow-y-auto">
          <div className="px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-[#6B7280] border-b border-[#2C2D35]">ALI subscriber matches · CAD history, facilities &amp; premises</div>
          {sugs.map((s, i) => (
            <button type="button" key={`${s.digits}-${i}`} data-testid={`ani-suggestion-${i}`} onClick={() => pick(s)}
              className="w-full text-left px-2.5 py-2 flex items-start gap-2 hover:bg-[#1c1d22] border-b border-[#1c1d22]">
              {s.kind === "prior caller" ? <User size={13} className="mt-0.5 text-[#93C5FD] shrink-0" /> : <Building2 size={13} className="mt-0.5 text-[#F59E0B] shrink-0" />}
              <span className="min-w-0 flex-1">
                <span className="block font-mono text-xs text-white">{s.phone} <span className="text-[#E5E7EB] font-head">— {s.name}</span>
                  {s.hits >= 3 && <span className="ml-1.5 px-1 font-mono text-[8px] font-bold bg-[#F59E0B]/20 text-[#FCD34D] border border-[#F59E0B]/50">REPEAT</span>}</span>
                <span className="block font-mono text-[9px] text-[#9CA3AF] truncate">{s.kind}{s.address ? ` · ${s.address}` : ""}{s.last_seen ? ` · last ${s.last_seen.slice(0, 10)}` : ""}{s.hits > 1 ? ` · ${s.hits} prior` : ""}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
